import { GRID_CONFIG, sortByAuditUpdatedOn } from "../config";
import axios from "axios";
import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import {
    FaSearch,
    FaTimes,
    FaAngleDoubleLeft,
    FaAngleLeft,
    FaAngleRight,
    FaAngleDoubleRight,
} from "react-icons/fa";
import "react-toastify/dist/ReactToastify.css";

import { notifyUser } from "../utils/notifyUser";

const QueryList = () => {
    const [queries, setQueries] = useState([]);
    const [search, setSearch] = useState("");
    const [perPage, setPerPage] = useState(GRID_CONFIG.defaultPageSize);
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [selectedQuery, setSelectedQuery] = useState(null);
    const [userFilter, setUserFilter] = useState("all");
    const [respondedFilter, setRespondedFilter] = useState("all");
    const customStyles = {
        headRow: {
            style: {
                backgroundColor: "var(--navy)",
                color: "white",
                fontWeight: "bold",
                fontSize: "16px",
                minHeight: "50px",
            },
        },
        rows: {
            style: {
                minHeight: "48px",
                "&:hover": {
                    backgroundColor: "#f2f9ff",
                },
            },
        },
    };
    const columns = [
        {
            name: "Submitter",
            selector: (row) => row.submitter_name || "Anonymous",
            sortable: true,
            width: "15%",
            cell: (row) => (
                <div className="flex items-center">
                    <span>{row.submitter_name || "Anonymous"}</span>
                    {!row.user_id && (
                        <span
                            className="ml-2 px-1.5 py-0.5 text-xs rounded-full"
                            style={{
                                backgroundColor: "#f3f4f6",
                                color: "#6b7280",
                                border: "1px solid #d1d5db",
                            }}
                        >
                            Guest
                        </span>
                    )}
                </div>
            ),
        },
        {
            name: "Email",
            selector: (row) => (row.user_id ? row.email || "N/A" : "Hidden"),
            sortable: true,
            width: "15%",
            cell: (row) => (
                <span>
                    {row.user_id ? (
                        row.email || "N/A"
                    ) : (
                        <span style={{ color: "#888" }}>Hidden</span>
                    )}
                </span>
            ),
        },
        {
            name: "Title",
            selector: (row) =>
                row.title.length > 30
                    ? `${row.title.substring(0, 30)}...`
                    : row.title,
            sortable: true,
            width: "20%",
        },
        {
            name: "Description",
            selector: (row) =>
                row.description.length > 50
                    ? `${row.description.substring(0, 50)}...`
                    : row.description,
            width: "20%",
        },
        {
            name: "Created At",
            selector: (row) => new Date(row.created_at).toLocaleString(),
            sortable: true,
            width: "13%",
        },
        {
            name: "Responded",
            selector: (row) => (row.responded ? "Yes" : "No"),
            sortable: true,
            width: "10%",
            cell: (row) =>
                row.responded ? (
                    <span style={{ color: "green", fontWeight: 600 }}>Yes</span>
                ) : (
                    <span style={{ color: "red", fontWeight: 600 }}>No</span>
                ),
        },
    ];

    useEffect(() => {
        const fetchQueries = async () => {
            try {
                const response = await axios.get(
                    `${process.env.REACT_APP_BACKEND_URL}/api/faq/user-queries`,
                    { withCredentials: true },
                );
                setQueries(sortByAuditUpdatedOn(response.data));
                setLoading(false);
            } catch(error) {
                console.error("Error fetching queries:", error);
                setLoading(false);
            }
        };
        fetchQueries();
    }, []);

    const filteredQueries = queries.filter((q) => {
        // First apply text search filter
        const matchesSearch =
            q.title.toLowerCase().includes(search.toLowerCase()) ||
            q.description.toLowerCase().includes(search.toLowerCase());

        // Then apply user type filter
        let matchesUserFilter = true;
        if(userFilter === "anonymous") {
            matchesUserFilter = !q.user_id;
        } else if(userFilter === "registered") {
            matchesUserFilter = !!q.user_id;
        }

        // Apply responded filter
        let matchesRespondedFilter = true;
        if(respondedFilter === "responded") {
            matchesRespondedFilter = q.responded === 1 || q.responded === true;
        } else if(respondedFilter === "not-responded") {
            matchesRespondedFilter = !q.responded;
        }

        return matchesSearch && matchesUserFilter && matchesRespondedFilter;
    });

    const totalPages = Math.ceil(filteredQueries.length / perPage);
    const paginatedData = filteredQueries.slice(
        (currentPage - 1) * perPage,
        currentPage * perPage,
    );

    const totalRecords = filteredQueries.length;

    const handleExportQueriesCSV = () => {
        const rows = filteredQueries.map((q) => ({
            Submitter: q.submitter_name || "Anonymous",
            Email: q.user_id ? q.email || "N/A" : "Hidden",
            Title: q.title || "",
            Description: q.description || "",
            CreatedAt: q.created_at ? new Date(q.created_at).toLocaleString() : "",
            Responded: q.responded ? "Yes" : "No",
        }));

        if (!rows.length) {
            notifyUser(null, "info", "No data to export", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        const header = Object.keys(rows[0]);
        const csv = [
            header.join(","),
            ...rows.map((r) => header.map((h) => `"${String(r[h]).replace(/"/g, '""')}"`).join(",")),
        ].join("\n");

        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "queries_export.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const CustomPagination = () => (
        <div className="flex justify-between items-center px-2 py-3 text-sm text-gray-700 mt-4">
            <span>
                Showing{" "}
                {totalRecords === 0 ? 0 : (currentPage - 1) * perPage + 1} to{" "}
                {Math.min(currentPage * perPage, totalRecords)} of{" "}
                {totalRecords} records
            </span>
            <div className="flex gap-2 text-lg items-center">
                <button
                    disabled={currentPage === 1 || loading}
                    onClick={() => setCurrentPage(1)}
                    className="disabled:opacity-40"
                >
                    <FaAngleDoubleLeft />
                </button>
                <button
                    disabled={currentPage === 1 || loading}
                    onClick={() =>
                        setCurrentPage((prev) => Math.max(prev - 1, 1))
                    }
                    className="disabled:opacity-40"
                >
                    <FaAngleLeft />
                </button>
                <span className="text-sm px-2">
                    Page {currentPage} of {totalPages}
                </span>
                <button
                    disabled={currentPage === totalPages || loading}
                    onClick={() =>
                        setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                    }
                    className="disabled:opacity-40"
                >
                    <FaAngleRight />
                </button>
                <button
                    disabled={currentPage === totalPages || loading}
                    onClick={() => setCurrentPage(totalPages)}
                    className="disabled:opacity-40"
                >
                    <FaAngleDoubleRight />
                </button>
            </div>
        </div>
    );

    return (
        <div
            className="aqi-grid-page"
            style={{
                padding: "30px",
                width: "90%",
                margin: "30px auto",
                boxShadow: "0 0 20px rgba(0,0,0,0.1)",
                borderRadius: "8px",
                backgroundColor: "white",
                position: "relative",
            }}
        >
            <h2
                style={{
                    color: "var(--navy)",
                    marginBottom: "20px",
                    fontSize: "large",
                    fontFamily: "large",
                }}
            >
                USER SUBMITTED QUERIES
            </h2>{" "}
            <div className="aqi-grid-toolbar">
                    {/* Records per page dropdown */}
                    <div>
                        <select
                            style={{
                                padding: "0.375rem 2rem 0.375rem 0.75rem",
                                color: "var(--navy)",
                                backgroundColor: "#F5F7FA",
                                border: "2px solid #C2D7E2",
                                borderRadius: "0.375rem",
                                fontWeight: "500",
                                appearance: "none",
                                backgroundImage:
                                    "var(--navy-select-chevron)",
                                backgroundRepeat: "no-repeat",
                                backgroundPosition: "right 0.5rem center",
                                backgroundSize: "16px 12px",
                                transition: "all 0.2s ease",
                                cursor: "pointer",
                                outline: "none",
                            }}
                            className="aqi-grid-control hover:border-navy focus:border-navy focus:ring-1 focus:ring-navy focus:shadow-sm"
                            value={perPage}
                            onChange={(e) => {
                                setPerPage(Number(e.target.value));
                                setCurrentPage(1);
                            }}
                        >
                            {GRID_CONFIG.pageSizeOptions.map((option) => (
                                <option key={option} value={option}>{option}</option>
                            ))}
                        </select>
                    </div>

                    {/* User type filter dropdown */}
                    <div>
                        <select
                            style={{
                                padding: "0.375rem 2rem 0.375rem 0.75rem",
                                color: "var(--navy)",
                                backgroundColor: "#F5F7FA",
                                border: "2px solid #C2D7E2",
                                borderRadius: "0.375rem",
                                fontWeight: "500",
                                appearance: "none",
                                backgroundImage:
                                    "var(--navy-select-chevron)",
                                backgroundRepeat: "no-repeat",
                                backgroundPosition: "right 0.5rem center",
                                backgroundSize: "16px 12px",
                                transition: "all 0.2s ease",
                                cursor: "pointer",
                                outline: "none",
                                minWidth: "180px",
                            }}
                            className="aqi-grid-control hover:border-navy focus:border-navy focus:ring-1 focus:ring-navy focus:shadow-sm"
                            value={userFilter}
                            onChange={(e) => {
                                setUserFilter(e.target.value);
                                setCurrentPage(1);
                            }}
                        >
                            <option value="all">All Users</option>
                            <option value="registered">Registered Users</option>
                            <option value="anonymous">Anonymous Users</option>
                        </select>
                    </div>

                    {/* Responded filter dropdown */}
                    <div>
                        <select
                            style={{
                                padding: "0.375rem 2rem 0.375rem 0.75rem",
                                color: "var(--navy)",
                                backgroundColor: "#F5F7FA",
                                border: "2px solid #C2D7E2",
                                borderRadius: "0.375rem",
                                fontWeight: "500",
                                appearance: "none",
                                backgroundImage:
                                    "var(--navy-select-chevron)",
                                backgroundRepeat: "no-repeat",
                                backgroundPosition: "right 0.5rem center",
                                backgroundSize: "16px 12px",
                                transition: "all 0.2s ease",
                                cursor: "pointer",
                                outline: "none",
                                minWidth: "180px",
                            }}
                            className="aqi-grid-control hover:border-navy focus:border-navy focus:ring-1 focus:ring-navy focus:shadow-sm"
                            value={respondedFilter}
                            onChange={(e) => {
                                setRespondedFilter(e.target.value);
                                setCurrentPage(1);
                            }}
                        >
                            <option value="all">All Responses</option>
                            <option value="responded">Responded</option>
                            <option value="not-responded">Not Responded</option>
                        </select>
                    </div>

                <div className="aqi-grid-search">
                    <FaSearch
                        className="absolute left-3 top-2.5"
                        style={{ color: "var(--navy)" }}
                    />
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value);
                            setCurrentPage(1);
                        }}
                        placeholder="Search queries..."
                        style={{
                            paddingLeft: "2.5rem",
                            paddingRight: "0.75rem",
                            borderRadius: "0.375rem",
                            border: "2px solid #C2D7E2",
                            backgroundColor: "#F5F7FA",
                            transition: "all 0.2s ease",
                            outline: "none",
                            color: "var(--navy)",
                        }}
                        className="aqi-grid-search-input hover:border-navy focus:border-navy focus:ring-1 focus:ring-navy focus:shadow-sm"
                    />
                </div>

                <div className="aqi-grid-toolbar-actions">
                    <button
                        onClick={handleExportQueriesCSV}
                        title="Export CSV"
                        className="aqi-grid-button aqi-grid-button--primary"
                    >
                        Export
                    </button>
                </div>
            </div>
            <DataTable
                columns={columns}
                data={paginatedData}
                customStyles={customStyles}
                progressPending={loading}
                pagination={false}
                striped
                responsive
                highlightOnHover
                onRowClicked={(row) => setSelectedQuery(row)}
            />
            <CustomPagination />
            {/* Query Detail Modal */}
            {selectedQuery && (
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: "rgba(0,0,0,0.6)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 1000,
                    }}
                >
                    <div
                        style={{
                            backgroundColor: "white",
                            padding: "30px",
                            borderRadius: "12px",
                            width: "80%",
                            maxWidth: "800px",
                            maxHeight: "90vh",
                            overflow: "auto",
                            position: "relative",
                            boxShadow: "0 0 30px rgba(0,0,0,0.3)",
                            border: "2px solid #C2D7E2",
                        }}
                    >
                        <button
                            onClick={() => setSelectedQuery(null)}
                            style={{
                                position: "absolute",
                                top: "15px",
                                right: "15px",
                                background: "var(--navy)",
                                border: "none",
                                borderRadius: "50%",
                                width: "30px",
                                height: "30px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                color: "white",
                                transition: "all 0.2s ease",
                            }}
                            onMouseOver={(e) =>
                                (e.currentTarget.style.backgroundColor =
                                    "#003658")
                            }
                            onMouseOut={(e) =>
                                (e.currentTarget.style.backgroundColor =
                                    "var(--navy)")
                            }
                        >
                            <FaTimes />
                        </button>

                        <h2
                            style={{
                                color: "var(--navy)",
                                marginBottom: "20px",
                                borderBottom: "2px solid #C2D7E2",
                                paddingBottom: "15px",
                                fontSize: "24px",
                                fontWeight: "600",
                            }}
                        >
                            Query Details
                        </h2>

                        <div
                            style={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: "20px",
                                marginBottom: "25px",
                            }}
                        >
                            <div
                                style={{
                                    padding: "15px",
                                    backgroundColor: "#F5F7FA",
                                    borderRadius: "8px",
                                    border: "1px solid #C2D7E2",
                                }}
                            >
                                <h3
                                    style={{
                                        color: "var(--navy)",
                                        marginBottom: "8px",
                                        fontSize: "16px",
                                        fontWeight: "600",
                                    }}
                                >
                                    User Information
                                </h3>
                                <p
                                    style={{
                                        margin: "5px 0",
                                        fontSize: "14px",
                                    }}
                                >
                                    <strong>User ID:</strong>{" "}
                                    {selectedQuery.user_id || "Anonymous"}
                                </p>
                                <p
                                    style={{
                                        margin: "5px 0",
                                        fontSize: "14px",
                                    }}
                                >
                                    <strong>Email:</strong>{" "}
                                    {selectedQuery.user_id ? (
                                        selectedQuery.email
                                    ) : (
                                        <span
                                            style={{
                                                color: "#888",
                                                fontStyle: "italic",
                                            }}
                                        >
                                            Hidden
                                        </span>
                                    )}
                                </p>
                                <p
                                    style={{
                                        margin: "5px 0",
                                        fontSize: "14px",
                                    }}
                                >
                                    <strong>Created At:</strong>{" "}
                                    {new Date(
                                        selectedQuery.created_at,
                                    ).toLocaleString()}
                                </p>
                            </div>

                            <div
                                style={{
                                    padding: "15px",
                                    backgroundColor: "#F5F7FA",
                                    borderRadius: "8px",
                                    border: "1px solid #C2D7E2",
                                }}
                            >
                                <h3
                                    style={{
                                        color: "var(--navy)",
                                        marginBottom: "8px",
                                        fontSize: "16px",
                                        fontWeight: "600",
                                    }}
                                >
                                    Response Status
                                </h3>
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        height: "100px",
                                    }}
                                >
                                    <div
                                        style={{
                                            padding: "10px 20px",
                                            borderRadius: "30px",
                                            backgroundColor:
                                                selectedQuery.responded
                                                    ? "rgba(0, 128, 0, 0.1)"
                                                    : "rgba(255, 0, 0, 0.1)",
                                            border: `1px solid ${
                                                selectedQuery.responded
                                                    ? "green"
                                                    : "red"
                                            }`,
                                            color: selectedQuery.responded
                                                ? "green"
                                                : "red",
                                            fontWeight: "bold",
                                            fontSize: "18px",
                                            textAlign: "center",
                                        }}
                                    >
                                        {selectedQuery.responded
                                            ? "Responded"
                                            : "Not Responded Yet"}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div
                            style={{
                                marginBottom: "25px",
                                padding: "20px",
                                backgroundColor: "#F5F7FA",
                                borderRadius: "8px",
                                border: "1px solid #C2D7E2",
                            }}
                        >
                            <h3
                                style={{
                                    color: "var(--navy)",
                                    marginBottom: "12px",
                                    fontSize: "18px",
                                    fontWeight: "600",
                                }}
                            >
                                Query Content
                            </h3>

                            <div style={{ marginBottom: "15px" }}>
                                <h4
                                    style={{
                                        color: "var(--navy)",
                                        marginBottom: "5px",
                                        fontWeight: "500",
                                    }}
                                >
                                    Title:
                                </h4>
                                <p
                                    style={{
                                        margin: 0,
                                        whiteSpace: "pre-wrap",
                                        wordWrap: "break-word",
                                        padding: "10px",
                                        backgroundColor: "white",
                                        border: "1px solid #C2D7E2",
                                        borderRadius: "4px",
                                        fontSize: "15px",
                                    }}
                                >
                                    {selectedQuery.title}
                                </p>
                            </div>

                            <div>
                                <h4
                                    style={{
                                        color: "var(--navy)",
                                        marginBottom: "5px",
                                        fontWeight: "500",
                                    }}
                                >
                                    Description:
                                </h4>
                                <p
                                    style={{
                                        margin: 0,
                                        whiteSpace: "pre-wrap",
                                        wordWrap: "break-word",
                                        overflowWrap: "break-word",
                                        padding: "10px",
                                        backgroundColor: "white",
                                        border: "1px solid #C2D7E2",
                                        borderRadius: "4px",
                                        minHeight: "100px",
                                        fontSize: "15px",
                                    }}
                                >
                                    {selectedQuery.description}
                                </p>
                            </div>
                        </div>

                        {/* Email Response Form */}
                        <div
                            style={{
                                marginTop: "20px",
                                padding: "20px",
                                backgroundColor: "#F5F7FA",
                                borderRadius: "8px",
                                border: "1px solid #C2D7E2",
                            }}
                        >
                            <h3
                                style={{
                                    color: "var(--navy)",
                                    marginBottom: "15px",
                                    fontSize: "18px",
                                    fontWeight: "600",
                                    borderBottom: "1px solid #C2D7E2",
                                    paddingBottom: "10px",
                                }}
                            >
                                Send Response
                            </h3>
                            <form
                                onSubmit={async (e) => {
                                    e.preventDefault();
                                    const subject =
                                        e.target.elements.subject.value;
                                    const message =
                                        e.target.elements.message.value;

                                    try {
                                        await axios.post(
                                            `${process.env.REACT_APP_BACKEND_URL}/api/faq/send-email-response`,
                                            {
                                                email: selectedQuery.email,
                                                subject: subject,
                                                message: message,
                                                queryId: selectedQuery.id,
                                            },
                                            { withCredentials: true },
                                        );
                                        notifyUser(null, "success", "Email sent successfully!", "toast", null, null, 3000, null, null, false, false, "top-center");
                                        setQueries((prevQueries) =>
                                            prevQueries.map((q) =>
                                                q.id === selectedQuery.id
                                                    ? { ...q, responded: 1 }
                                                    : q,),);
                                        setSelectedQuery(null);
                                    } catch(error) {
                                        console.error(
                                            "Error sending email response:",
                                            error,
                                        );
                                        notifyUser(null, "error", "Failed to send email response.", "toast", null, null, 3000, null, null, false, false, "top-center");
                                    }
                                }}
                            >
                                <div style={{ marginBottom: "15px" }}>
                                    <label
                                        htmlFor="subject"
                                        style={{
                                            display: "block",
                                            marginBottom: "5px",
                                            color: "var(--navy)",
                                            fontWeight: "500",
                                            fontSize: "14px",
                                        }}
                                    >
                                        Email Subject:
                                    </label>
                                    <input
                                        id="subject"
                                        type="text"
                                        name="subject"
                                        placeholder="Enter email subject..."
                                        style={{
                                            width: "100%",
                                            padding: "12px 15px",
                                            marginBottom: "10px",
                                            border: "2px solid #C2D7E2",
                                            borderRadius: "6px",
                                            fontSize: "14px",
                                            transition: "all 0.2s ease",
                                            backgroundColor: "white",
                                            outline: "none",
                                        }}
                                        className="hover:border-navy focus:border-navy focus:ring-1 focus:ring-navy focus:shadow-sm"
                                        required
                                    />
                                </div>
                                <div style={{ marginBottom: "15px" }}>
                                    <label
                                        htmlFor="message"
                                        style={{
                                            display: "block",
                                            marginBottom: "5px",
                                            color: "var(--navy)",
                                            fontWeight: "500",
                                            fontSize: "14px",
                                        }}
                                    >
                                        Email Message:
                                    </label>
                                    <textarea
                                        id="message"
                                        name="message"
                                        rows="8"
                                        placeholder="Type your response here..."
                                        style={{
                                            width: "100%",
                                            padding: "12px 15px",
                                            border: "2px solid #C2D7E2",
                                            borderRadius: "6px",
                                            marginBottom: "15px",
                                            fontSize: "14px",
                                            resize: "vertical",
                                            transition: "all 0.2s ease",
                                            backgroundColor: "white",
                                            outline: "none",
                                        }}
                                        className="hover:border-navy focus:border-navy focus:ring-1 focus:ring-navy focus:shadow-sm"
                                        required
                                    ></textarea>
                                </div>
                                <div style={{ textAlign: "right" }}>
                                    <button
                                        type="submit"
                                        style={{
                                            backgroundColor: "var(--navy)",
                                            color: "white",
                                            padding: "12px 24px",
                                            border: "none",
                                            borderRadius: "6px",
                                            cursor: "pointer",
                                            fontSize: "15px",
                                            fontWeight: "500",
                                            transition: "all 0.2s ease",
                                            boxShadow:
                                                "0 2px 4px rgba(0,0,0,0.1)",
                                        }}
                                        onMouseOver={(e) =>
                                            (e.currentTarget.style.backgroundColor =
                                                "#003658")
                                        }
                                        onMouseOut={(e) =>
                                            (e.currentTarget.style.backgroundColor =
                                                "var(--navy)")
                                        }
                                    >
                                        Send Response
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default QueryList;